import { useMutation, useQuery } from "@apollo/client/react";
import {
  Button,
  Group,
  Loader,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconSend } from "@tabler/icons-react";
import { gql } from "@apollo/client";
import { graphql, ResultOf } from "gql.tada";
import { useState } from "react";
import { PageHeader } from "../../components/PageHeader";

const EMAIL_TEMPLATE_CONFIGURATIONS_QUERY = graphql(`
  query EmailSendTemplateConfigurations {
    emailTemplateConfigurations {
      name
      description
      triggerEvent
      availableVariables {
        description
        name
      }
    }
  }
`);

const IDENTITY_PROVIDERS_QUERY = graphql(`
  query EmailSendIdentityProviders {
    identityProviders {
      ... on OrganizationIdentityProvider {
        id
        name
        configuration {
          ... on OAuthClientConfiguration {
            dataRetrievers {
              mappingConfiguration {
                mappings {
                  mappedTo
                }
              }
            }
          }
        }
      }
    }
  }
`);

const SEARCH_PLAYER_QUERY = gql`
  query SearchPlayerForEmail($username: String!) {
    searchPlayer(username: $username) {
      id
      username
    }
  }
`;

const EMAIL_SENT_MUTATION = gql`
  mutation EmailSent(
    $fromTemplate: EmailSentFromTemplateInput
    $fromCustom: EmailSentFromCustomInput
  ) {
    emailSent(fromTemplate: $fromTemplate, fromCustom: $fromCustom)
  }
`;

type Player = { id: string; username: string };

const PlayerSearch = ({
  selectedPlayer,
  onChange,
}: {
  selectedPlayer: Player | null;
  onChange: (player: Player | null) => void;
}) => {
  const [searchValue, setSearchValue] = useState("");
  const { refetch } = useQuery<{ searchPlayer: Player[] }>(
    SEARCH_PLAYER_QUERY,
    { skip: true },
  );
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [searching, setSearching] = useState(false);

  const handleSearch = async (value: string) => {
    setSearchValue(value);
    if (value.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const result = await refetch({ username: value });
      setSearchResults(result.data?.searchPlayer ?? []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <Select
      label="Target Player (optional)"
      description="Leave empty to send to all players, or search for a specific player"
      placeholder="Type a username to search..."
      searchable
      clearable
      nothingFoundMessage={searching ? "Searching..." : "No players found"}
      onSearchChange={handleSearch}
      searchValue={searchValue}
      data={searchResults.map((p) => ({
        label: p.username,
        value: p.id,
      }))}
      onChange={(value) => {
        if (value) {
          const player = searchResults.find((p) => p.id === value);
          if (player) onChange(player);
        } else {
          onChange(null);
        }
      }}
      value={selectedPlayer?.id ?? null}
      rightSection={searching ? <Loader size="xs" /> : undefined}
    />
  );
};

const PropertySourceConfig = ({
  propertySource,
  propertySourceId,
  property,
  identityProviders,
  onChange,
}: {
  propertySource: "IDENTITY_PROVIDER" | "PLAYER";
  propertySourceId?: string;
  property: string;
  identityProviders: ResultOf<
    typeof IDENTITY_PROVIDERS_QUERY
  >["identityProviders"];
  onChange: (config: {
    property: string;
    propertySourceId?: string;
    propertySource: "IDENTITY_PROVIDER" | "PLAYER";
  }) => void;
}) => {
  const [sourceValue, setSourceValue] = useState(propertySource);
  const [sourceIdValue, setSourceIdValue] = useState(propertySourceId);
  const [propertyValue, setPropertyValue] = useState(property);

  const update = (
    newSource: typeof sourceValue,
    newSourceId: typeof sourceIdValue,
    newProperty: string,
  ) => {
    onChange({
      property: newProperty,
      propertySourceId: newSourceId,
      propertySource: newSource,
    });
  };

  return (
    <>
      <Select
        label="Property Source"
        placeholder="Select a property source"
        data={[
          { label: "Identity Provider", value: "IDENTITY_PROVIDER" },
          { label: "Player", value: "PLAYER" },
        ]}
        value={sourceValue}
        onChange={(value) => {
          const v = value as "IDENTITY_PROVIDER" | "PLAYER";
          setSourceValue(v);
          update(v, sourceIdValue, propertyValue);
        }}
      />
      {sourceValue === "IDENTITY_PROVIDER" && (
        <>
          <Select
            label="Identity Provider"
            placeholder="Select an identity provider"
            data={identityProviders.map((idp) => ({
              label:
                idp.__typename === "OrganizationIdentityProvider"
                  ? idp.name
                  : "",
              value:
                idp.__typename === "OrganizationIdentityProvider" ? idp.id : "",
            }))}
            value={sourceIdValue}
            onChange={(value) => {
              setSourceIdValue(value ?? undefined);
              update(sourceValue, value ?? undefined, propertyValue);
            }}
          />
          {sourceIdValue &&
            (() => {
              const idp = identityProviders.find(
                (idp) =>
                  idp.__typename === "OrganizationIdentityProvider" &&
                  idp.id === sourceIdValue,
              );
              return (
                <Select
                  label="Property"
                  placeholder="Select a property"
                  data={
                    (idp?.__typename === "OrganizationIdentityProvider" &&
                    idp.configuration?.__typename === "OAuthClientConfiguration"
                      ? idp.configuration?.dataRetrievers.flatMap(
                          (dataRetriever) =>
                            dataRetriever.mappingConfiguration?.mappings.map(
                              (mapping) => ({
                                label: mapping.mappedTo,
                                value: mapping.mappedTo,
                              }),
                            ),
                        )
                      : []) ?? []
                  }
                  value={propertyValue}
                  onChange={(value) => {
                    setPropertyValue(value ?? "");
                    update(sourceValue, sourceIdValue, value ?? "");
                  }}
                />
              );
            })()}
        </>
      )}
      {sourceValue === "PLAYER" && (
        <TextInput
          label="Property"
          placeholder="Enter a property (e.g. email)"
          value={propertyValue}
          onChange={(event) => {
            setPropertyValue(event.target.value);
            update(sourceValue, sourceIdValue, event.target.value);
          }}
        />
      )}
    </>
  );
};

export const EmailSend = () => {
  const theme = useMantineTheme();
  const [mode, setMode] = useState<"template" | "custom">("template");
  const [sending, setSending] = useState(false);

  // Template mode state
  const [triggerEvent, setTriggerEvent] = useState<string | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [templatePlayer, setTemplatePlayer] = useState<Player | null>(null);

  // Custom mode state
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [emailField, setEmailField] = useState<{
    property: string;
    propertySourceId?: string;
    propertySource: "IDENTITY_PROVIDER" | "PLAYER";
  }>({ property: "", propertySource: "PLAYER" });
  const [customPlayer, setCustomPlayer] = useState<Player | null>(null);

  const { data: configurationsData, loading: configurationsLoading } =
    useQuery<any>(EMAIL_TEMPLATE_CONFIGURATIONS_QUERY);
  const { data: idpData, loading: idpLoading } = useQuery<any>(
    IDENTITY_PROVIDERS_QUERY,
  );
  const [emailSentMutation] = useMutation<any>(EMAIL_SENT_MUTATION);

  if (configurationsLoading || idpLoading) {
    return <Loader />;
  }

  const selectedConfig = configurationsData?.emailTemplateConfigurations.find(
    (c: any) => c.triggerEvent === triggerEvent,
  );

  const handleSubmit = async () => {
    setSending(true);
    try {
      if (mode === "template") {
        if (!triggerEvent) {
          notifications.show({
            title: "Error",
            message: "Please select a trigger event",
            color: "red",
          });
          return;
        }

        const variablesInput = Object.entries(variables)
          .filter(([, value]) => value.length > 0)
          .map(([property, value]) => ({ property, value }));

        await emailSentMutation({
          variables: {
            fromTemplate: {
              triggerEvent,
              variables: variablesInput,
              ...(templatePlayer && {
                playerIds: templatePlayer.id,
              }),
            },
          },
        }).then((data) => {
          if (data?.error) {
            throw new Error(data.error.message);
          }
        });
      } else {
        if (!subject || !body) {
          notifications.show({
            title: "Error",
            message: "Please fill in subject and body",
            color: "red",
          });
          return;
        }
        if (!emailField.property) {
          notifications.show({
            title: "Error",
            message: "Please configure the email field",
            color: "red",
          });
          return;
        }

        await emailSentMutation({
          variables: {
            fromCustom: {
              subject,
              body,
              emailField: {
                property: emailField.property,
                propertySource: emailField.propertySource,
                ...(emailField.propertySourceId && {
                  propertySourceId: emailField.propertySourceId,
                }),
              },
              ...(customPlayer && {
                playerIds: customPlayer.id,
              }),
            },
          },
        }).then((data) => {
          if (data?.error) {
            throw new Error(data.error.message);
          }
        });
      }

      notifications.show({
        title: "Success",
        message: "Email sent successfully",
        color: "green",
        autoClose: 3000,
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to send email";
      notifications.show({
        title: "Error",
        message,
        color: "red",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Send Email"
        subtitle="Send emails to players using a template or custom content"
        showBreadcrumbs={false}
      />

      <Stack gap="lg">
        <SegmentedControl
          value={mode}
          onChange={(value) => setMode(value as "template" | "custom")}
          data={[
            { label: "From Template", value: "template" },
            { label: "Custom", value: "custom" },
          ]}
        />

        {mode === "template" && (
          <Stack gap="md">
            <Select
              label="Trigger Event"
              placeholder="Select a trigger event"
              data={
                configurationsData?.emailTemplateConfigurations.map(
                  (c: any) => ({
                    label: c.name,
                    value: c.triggerEvent,
                  }),
                ) ?? []
              }
              value={triggerEvent}
              onChange={(value) => {
                setTriggerEvent(value);
                setVariables({});
              }}
            />

            {selectedConfig && (
              <>
                <Text size="sm" c="dimmed">
                  {selectedConfig.description}
                </Text>

                {selectedConfig.availableVariables.length > 0 && (
                  <>
                    <Title order={5}>Template Variables</Title>
                    {selectedConfig.availableVariables.map((variable: any) => (
                      <TextInput
                        key={variable.name}
                        label={variable.name}
                        description={variable.description}
                        placeholder={`Value for {{${variable.name}}}`}
                        value={variables[variable.name] ?? ""}
                        onChange={(e) =>
                          setVariables({
                            ...variables,
                            [variable.name]: e.target.value,
                          })
                        }
                      />
                    ))}
                  </>
                )}
              </>
            )}

            <PlayerSearch
              selectedPlayer={templatePlayer}
              onChange={setTemplatePlayer}
            />
          </Stack>
        )}

        {mode === "custom" && (
          <Stack gap="md">
            <TextInput
              label="Subject"
              placeholder="Email subject line"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />

            <Textarea
              label="Body (HTML)"
              placeholder="Enter HTML email body..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              minRows={12}
              autosize
              styles={{
                input: {
                  fontFamily: "monospace",
                  fontSize: theme.fontSizes.sm,
                  backgroundColor: theme.colors.dark[7],
                  border: `1px solid ${theme.colors.dark[4]}`,
                  color: theme.colors.dark[0],
                },
              }}
            />

            <Title order={5}>Email Field Configuration</Title>
            <PropertySourceConfig
              propertySource={emailField.propertySource}
              propertySourceId={emailField.propertySourceId}
              property={emailField.property}
              identityProviders={idpData?.identityProviders ?? []}
              onChange={setEmailField}
            />

            <PlayerSearch
              selectedPlayer={customPlayer}
              onChange={setCustomPlayer}
            />
          </Stack>
        )}

        <Group>
          <Button
            leftSection={<IconSend size={16} />}
            onClick={handleSubmit}
            loading={sending}
          >
            Send Email
          </Button>
        </Group>
      </Stack>
    </div>
  );
};
